import { useEffect, useState } from 'react';
import { Loader2, Download, X } from 'lucide-react';
import { api } from '../api/client';
import { Markdown } from '../lib/markdown';

interface Props {
  attachmentId: string;
  onClose: () => void;
}

interface Meta {
  id: string;
  filename: string;
  content_type: string | null;
  size_bytes: number;
}

const IMG_EXT = /\.(jpe?g|png|gif|webp|bmp|svg)$/i;
const MD_EXT = /\.(md|markdown)$/i;
const TEXT_EXT = /\.(txt|log|csv|tsv|json|xml|yaml|yml|ini|conf|sql|py|js|ts|tsx|jsx|css|html?)$/i;

export default function AttachmentPreviewModal({ attachmentId, onClose }: Props) {
  const [meta, setMeta] = useState<Meta | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    const load = async () => {
      setBusy(true); setErr(null);
      try {
        // 1. метаданные
        const all = await api.get<Meta[]>('/api/v1/attachments',
          { params: { owner_type: '_byid', owner_id: attachmentId } }).catch(() => null);
        // Ленивая стратегия: meta-endpoint у нас нет, поэтому сразу качаем blob.
        const r = await api.get(`/api/v1/attachments/${attachmentId}/download`,
                                { responseType: 'blob' });
        const ct = String(r.headers['content-type'] || '');
        // filename из Content-Disposition
        const cd = String(r.headers['content-disposition'] || '');
        const mfn = cd.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)/i);
        const filename = mfn ? decodeURIComponent(mfn[1]) : 'file';
        const size = (r.data as Blob).size;
        if (cancelled) return;

        if (IMG_EXT.test(filename) || ct.startsWith('image/')) {
          createdUrl = URL.createObjectURL(r.data as Blob);
          setBlobUrl(createdUrl);
        } else if (MD_EXT.test(filename) || TEXT_EXT.test(filename) || ct.startsWith('text/')) {
          const text = await (r.data as Blob).text();
          setTextContent(text);
          createdUrl = URL.createObjectURL(r.data as Blob);
          setBlobUrl(createdUrl);
        } else {
          // Бинарь — превью невозможно. Делаем download.
          createdUrl = URL.createObjectURL(r.data as Blob);
          setBlobUrl(createdUrl);
        }
        setMeta({ id: attachmentId, filename, content_type: ct, size_bytes: size });
      } catch (e: any) {
        if (!cancelled) setErr(e?.response?.data?.detail || e?.message || 'Не удалось загрузить файл');
      } finally {
        if (!cancelled) setBusy(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attachmentId]);

  const download = () => {
    if (!blobUrl || !meta) return;
    setDownloading(true);
    const a = document.createElement('a');
    a.href = blobUrl; a.download = meta.filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => setDownloading(false), 800);
  };

  const ext = (meta?.filename || '').split('.').pop()?.toLowerCase() || '';
  const isImage = meta && (IMG_EXT.test(meta.filename) || (meta.content_type || '').startsWith('image/'));
  const isMd = meta && MD_EXT.test(meta.filename);
  const isText = meta && (TEXT_EXT.test(meta.filename) || (meta.content_type || '').startsWith('text/'));
  const canPreview = isImage || isMd || isText;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-5xl w-full max-h-[90vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <header className="p-3 border-b border-slate-800 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-white truncate">
              {meta?.filename || 'Загрузка…'}
            </div>
            {meta && (
              <div className="text-[11px] text-slate-500">
                {ext.toUpperCase()} · {(meta.size_bytes / 1024).toFixed(1)} КБ
              </div>
            )}
          </div>
          <button onClick={download} disabled={!blobUrl || downloading}
            className="text-xs inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-3 py-1.5 rounded">
            {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            Скачать
          </button>
          <button onClick={onClose}
            className="text-slate-400 hover:text-white p-1.5 rounded hover:bg-slate-800">
            <X size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-auto p-4 min-h-[200px]">
          {busy ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="animate-spin text-blue-400" />
            </div>
          ) : err ? (
            <div className="bg-red-900/30 border border-red-800 text-red-200 px-3 py-2 rounded text-sm">
              {err}
            </div>
          ) : !canPreview ? (
            <div className="text-center py-12 text-slate-400">
              <div className="text-sm mb-3">
                Превью для формата <code>.{ext}</code> не поддерживается.
              </div>
              <button onClick={download}
                className="text-sm inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded">
                <Download size={14} /> Скачать «{meta?.filename}»
              </button>
            </div>
          ) : isImage ? (
            <div className="flex items-center justify-center">
              <img src={blobUrl!} alt={meta!.filename}
                   className="max-w-full max-h-[75vh] object-contain rounded" />
            </div>
          ) : isMd && textContent != null ? (
            <div className="prose-invert max-w-3xl mx-auto"><Markdown source={textContent} /></div>
          ) : (
            <pre className="text-xs text-slate-200 whitespace-pre-wrap font-mono max-w-4xl mx-auto">
{textContent}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
